import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Smartphone, Loader2, Save, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function LinkInput({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="flex-1 h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#FFC400] focus:ring-2 focus:ring-[#FFC400]/30" />
        {value && (
          <a href={value} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-open`}
            className="grid place-items-center h-11 w-11 rounded-lg border border-slate-200 text-slate-500 hover:text-[#FF5A4D]">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}

export const AppLinksCard = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ app_store_url: "", play_store_url: "" });
  const { data } = useQuery({ queryKey: ["app-links"], queryFn: () => api.get("/public/app-links") });
  useEffect(() => { if (data) setForm(data); }, [data]);

  const saveMut = useMutation({
    mutationFn: (body) => api.put("/admin/app-links", body),
    onSuccess: () => { toast.success("Store links saved"); qc.invalidateQueries({ queryKey: ["app-links"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="app-links-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="grid place-items-center h-11 w-11 rounded-xl bg-[#FFF4CC] text-[#1A1500]"><Smartphone className="h-5 w-5" /></div>
        <div>
          <h3 className="font-head text-base font-bold text-slate-900">Mobile App Store Links</h3>
          <p className="text-sm text-slate-500 mt-0.5">Shown on the public landing page. Leave empty to show “Coming soon”.</p>
        </div>
      </div>
      <div className="space-y-3">
        <LinkInput label="Apple App Store URL" testid="app-store-url-input" value={form.app_store_url}
          onChange={(v) => setForm((f) => ({ ...f, app_store_url: v }))} placeholder="https://apps.apple.com/app/..." />
        <LinkInput label="Google Play Store URL" testid="play-store-url-input" value={form.play_store_url}
          onChange={(v) => setForm((f) => ({ ...f, play_store_url: v }))} placeholder="https://play.google.com/store/apps/details?id=..." />
      </div>
      <button data-testid="app-links-save-btn" disabled={saveMut.isPending}
        onClick={() => saveMut.mutate({ app_store_url: form.app_store_url.trim(), play_store_url: form.play_store_url.trim() })}
        className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#FFC400] hover:bg-[#EAB000] text-[#1A1500] text-sm font-bold transition-colors disabled:opacity-60">
        {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save links
      </button>
    </div>
  );
};

export default AppLinksCard;
